//frontend/src/components/admin/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
    DollarSign, 
    ShoppingBag, 
    Package, 
    Users, 
    Truck, 
    AlertTriangle, 
    CheckSquare, 
    FileText,
    RefreshCw
} from 'lucide-react';
import axios from 'axios';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [stats, setStats] = useState({
        totalAmount: 0,
        totalOrders: 0,
        totalProducts: 0,
        totalUsers: 0,
        totalDeliveryStaff: 0,
        deliveredOrdersCount: 0,
        unassignedOrdersCount: 0,
        onlinePaymentsAmount: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/dashboard-stats', { withCredentials: true });
            const staffData = await axios.get('/api/v1/admin/delivery-staff', { withCredentials: true });
            const deliveredData = await axios.get('/api/v1/admin/delivered-orders', { withCredentials: true });

            const totalDeliveryStaff = staffData.data.deliveryStaff?.length || 0;
            const deliveredOrdersCount = deliveredData.data.orders?.length || 0;

            if (data.success) {
                setStats({
                    ...data.stats,
                    totalDeliveryStaff,
                    deliveredOrdersCount
                });
            }
        } catch (err) {
            console.error('Dashboard data fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon: Icon, onClick, subText }) => (
        <div 
            onClick={onClick}
            className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 cursor-pointer"
        >
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
                    <div className="p-1.5 rounded-lg bg-red-50">
                        <Icon className="w-4 h-4 text-[#dc2626]" />
                    </div>
                </div>
                <div className="mt-1">
                    <p className="text-xl font-bold text-gray-900">{value}</p>
                    <p className="text-xs text-gray-500 mt-1 hover:text-[#dc2626]">{subText}</p>
                </div>
            </div>
        </div>
    );

    const dashboardStats = [
        {
            title: "Total Revenue",
            value: `₹${stats.totalAmount?.toLocaleString()}`,
            icon: DollarSign,
            onClick: () => navigate('/admin/orders'),
            subText: "View all transactions"
        },
        {
            title: "Total Orders",
            value: stats.totalOrders,
            icon: ShoppingBag,
            onClick: () => navigate('/admin/orders'),
            subText: "Manage orders"
        },
        {
            title: "Total Products",
            value: stats.totalProducts,
            icon: Package,
            onClick: () => navigate('/admin/products'),
            subText: "View inventory"
        },
        {
            title: "Total Users",
            value: stats.totalUsers,
            icon: Users,
            onClick: () => navigate('/admin/users'),
            subText: "Manage customers"
        },
        {
            title: "Delivery Staff",
            value: stats.totalDeliveryStaff,
            icon: Truck,
            onClick: () => navigate('/delivery-staff-list'),
            subText: "Manage delivery team"
        },
        {
            title: "Unassigned Orders",
            value: stats.unassignedOrdersCount || 0,
            icon: AlertTriangle,
            onClick: () => navigate('/admin/unassigned-orders'),
            subText: "Needs attention"
        },
        {
            title: "Delivered Orders",
            value: stats.deliveredOrdersCount || 0,
            icon: CheckSquare,
            onClick: () => navigate('/admin/delivered-orders'),
            subText: "View completed deliveries"
        },
        {
            title: "Online Payments",
            value: stats.onlinePaymentsCount || 0,
            icon: DollarSign,
            onClick: () => navigate('/admin/online-payments'),
            subText: "View all online payments"
        },
        {
            title: "Reports",
            value: "Generate",
            icon: FileText,
            onClick: () => navigate('/admin/reports'),
            subText: "Download reports"
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-xl font-bold text-gray-800 mb-6 font-['Crimson_Text']">Dashboard Overview</h1>
                
                {/* Main Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {dashboardStats.map((stat, index) => (
                        <StatCard key={index} {...stat} />
                    ))}
                </div>

            </div>
        </div>
    );
};

export default AdminDashboard;